/**
 * Globale ErrorBoundary – Production-Härtung (Juni 2026).
 *
 * Fängt Render-/Lifecycle-Fehler aus dem React-Baum ab und zeigt statt eines
 * weißen Bildschirms eine verständliche Meldung mit „Neu laden". Es werden
 * KEINE sensiblen Daten angezeigt, kein API-Zugriff, kein externes Logging.
 *
 * Hinweis: Globale window-Fehler (z. B. cross-origin „Script error." oder der
 * WebKit-Fokus-Effekt) werden NICHT von React-ErrorBoundaries erfasst – dafür
 * sorgt der Fokus-Guard (`lib/focusGuard.js`). Diese Boundary ist die zusätzliche
 * Absicherung für echte App-Render-Fehler.
 */
import React from "react";
import { AlertTriangle } from "lucide-react";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Bewusst nur lokale Konsole, keine sensiblen Daten, kein externer Versand.
    if (typeof console !== "undefined") {
      console.error("App-Fehler abgefangen (ErrorBoundary):", error, info);
    }
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
      >
        <div className="max-w-md w-full text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">
            Es ist ein unerwarteter Fehler aufgetreten
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Ihre Daten sind sicher gespeichert. Bitte laden Sie die Seite neu –
            in den meisten Fällen ist danach alles wieder in Ordnung.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            data-testid="app-error-reload-button"
            className="mt-6 inline-flex items-center justify-center rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-slate-800"
          >
            Seite neu laden
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
